import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useAction, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, relative, shortId, text } from "@/lib/format";

interface Block {
  id: string;
  location_id: string;
  reason: string;
  scope: string;
  status: string;
  source_event?: string;
  source_entity_id?: string;
  note?: string;
  opened_by?: string;
  closed_by?: string;
  version: number;
  created_at: string;
  closed_at?: string;
}

type StatusFilter = "ACTIVE" | "CLOSED" | "";

export function Blocks() {
  const session = useSession();
  const [status, setStatus] = useState<StatusFilter>("ACTIVE");
  const [search, setSearch] = useState("");
  const [closing, setClosing] = useState<Block | null>(null);

  const query = useList<Block>(
    "inventory",
    "/api/v1/location-blocks",
    { status: status || undefined, limit: 200 },
  );

  const close = useAction<Block, { block: Block; reason: string }>(
    "inventory",
    ({ block, reason }) => ({
      path: `/api/v1/location-blocks/${block.id}/close`,
      method: "POST",
      // Versiya YUBORILADI: oraliqda blok o'zgargan bo'lsa server
      // `409` qaytaradi va biz uni bosib yozmaymiz (§11.2).
      body: { reason, version: block.version },
    }),
  );

  const canClose = session.can(PERM.blockClose);
  const needle = search.trim().toLowerCase();
  const rows = (query.data?.items ?? []).filter((row) =>
    needle
      ? [row.reason, row.scope, row.note, row.location_id, row.source_event]
          .some((value) => value?.toLowerCase().includes(needle))
      : true,
  );

  const columns: Array<Column<Block>> = [
    {
      key: "location",
      header: "Manzil",
      render: (row) => (
        <span className="mono" title={row.location_id}>
          {shortId(row.location_id)}
        </span>
      ),
    },
    { key: "reason", header: "Sabab", render: (row) => <StatusBadge value={row.reason} /> },
    {
      key: "scope",
      header: "Qamrov",
      // Qamrov blok NIMANI to'sishini bildiradi: PICK terishni,
      // PUTAWAY joylashtirishni, ALL ikkalasini.
      render: (row) => <StatusBadge value={row.scope} tone="tag" />,
    },
    { key: "status", header: "Holat", render: (row) => <StatusBadge value={row.status} /> },
    {
      key: "source",
      header: "Manba",
      render: (row) => (
        <span title={row.source_entity_id}>{text(row.source_event)}</span>
      ),
    },
    { key: "note", header: "Izoh", render: (row) => text(row.note) },
    {
      key: "opened",
      header: "Ochildi",
      render: (row) => <span title={dateTime(row.created_at)}>{relative(row.created_at)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) =>
        row.status === "ACTIVE" && canClose ? (
          <button
            type="button"
            className="btn btn-outlined btn-sm"
            onClick={() => setClosing(row)}
          >
            Yopish
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Bloklangan manzillar"
        hint="Bloklangan yacheykadagi qoldiq mavjud deb hisoblanmaydi (DAT-001). Blok manbasi ko'rsatilgan bo'lsa, uni faqat o'sha manba yopadi (STA-003)."
      />

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Sabab, qamrov yoki izoh bo'yicha…"
        onRefresh={() => void query.refetch()}
        refreshing={query.isFetching}
      >
        <SegmentedFilter
          value={status}
          onChange={setStatus}
          options={[
            { value: "ACTIVE", label: "Faol" },
            { value: "CLOSED", label: "Yopilgan" },
            { value: "", label: "Hammasi" },
          ]}
        />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => void query.refetch()}
        emptyTitle={status === "ACTIVE" ? "Faol blok yo'q" : "Blok topilmadi"}
        emptyHint="Bloklangan manzil bo'lmasa, barcha qoldiq terish uchun ochiq."
        hasMore={query.data?.has_more}
        footer={<span>{rows.length} ta blok</span>}
      />

      <ConfirmDialog
        open={closing !== null}
        title="Blokni yopish"
        description={
          closing ? (
            <>
              <span className="mono">{shortId(closing.location_id)}</span> manzilidagi{" "}
              <b>{closing.reason}</b> bloki yopiladi va yacheykadagi qoldiq yana
              mavjud bo'lib qoladi.
            </>
          ) : null
        }
        confirmLabel="Blokni yopish"
        danger
        requireReason
        reasonPlaceholder="Masalan: GTD rasmiylashtirildi"
        busy={close.isPending}
        error={close.error}
        onClose={() => {
          setClosing(null);
          close.reset();
        }}
        onConfirm={(reason) => {
          if (!closing) return;
          close.mutate(
            { block: closing, reason },
            {
              onSuccess: () => {
                setClosing(null);
                close.reset();
              },
            },
          );
        }}
      />
    </>
  );
}
